import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import useUrlState from '../../hooks/useUrlState';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const BIRIM_LABELS = { KG: 'kg', ADET: 'adet', LT: 'lt', PAKET: 'paket', KOLI: 'koli', PORSIYON: 'porsiyon' };
const getBirimLabel = (birim) => BIRIM_LABELS[birim] || (birim || 'kg').toLowerCase();

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const HAREKET_TIPI_LABELS = {
  GIRIS: 'Giriş',
  CIKIS: 'Çıkış',
  SAYIM_FARKI: 'Sayim Farki',
  FIRE: 'Fire',
  URETIM_GIRIS: 'Üretim Giriş',
  URETIM_CIKIS: 'Üretim Çıkış',
  SATIS: 'Satış'
};

const StokHareketleriPage = () => {
  const { user } = useAuth();

  const [hareketler, setHareketler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  // Filtreler URL'e bağlı (her biri ayrı query param): detaya/başka sayfaya gidip
  // geri dönünce / yenileyince korunur, menüden açınca temiz.
  const [lokasyonId, setLokasyonId] = useUrlState('lokasyon', '');
  const [malzemeId, setMalzemeId] = useUrlState('malzeme', '');
  const [hareketTipi, setHareketTipi] = useUrlState('tip', '');
  const [baslangic, setBaslangic] = useUrlState('baslangic', '');
  const [bitis, setBitis] = useUrlState('bitis', '');
  const filtre = {
    lokasyon_id: lokasyonId,
    malzeme_id: malzemeId,
    hareket_tipi: hareketTipi,
    baslangic_tarihi: baslangic,
    bitis_tarihi: bitis,
  };
  const FILTRE_SETTERS = {
    lokasyon_id: setLokasyonId,
    malzeme_id: setMalzemeId,
    hareket_tipi: setHareketTipi,
    baslangic_tarihi: setBaslangic,
    bitis_tarihi: setBitis,
  };
  const [manuelForm, setManuelForm] = useState({
    lokasyon_id: '',
    malzeme_id: '',
    hareket_tipi: 'SAYIM_FARKI',
    miktar: '',
    açıklama: ''
  });

  const fetchHareketler = useCallback((params = {}) => {
    setLoading(true);
    const queryParams = new URLSearchParams();
    if (params.lokasyon_id) queryParams.append('lokasyon_id', params.lokasyon_id);
    if (params.malzeme_id) queryParams.append('malzeme_id', params.malzeme_id);
    if (params.hareket_tipi) queryParams.append('hareket_tipi', params.hareket_tipi);
    if (params.baslangic_tarihi) queryParams.append('baslangic_tarihi', params.baslangic_tarihi);
    if (params.bitis_tarihi) queryParams.append('bitis_tarihi', params.bitis_tarihi);

    queryParams.append('limit', '200');
    fetch(`${API_URL}/api/stok-hareketleri?${queryParams.toString()}`, { headers: getAuthHeaders() })
      .then(res => {
        if (res.status === 401) { window.location.href = '/login'; return []; }
        return res.json();
      })
      .then(data => {
        setHareketler(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setHareketler([]);
        setLoading(false);
      });
  }, []);

  // Verileri yukle
  useEffect(() => {
    const headers = getAuthHeaders();

    fetch(`${API_URL}/api/lokasyonlar`, { headers })
      .then(res => res.json())
      .then(data => setLokasyonlar(Array.isArray(data) ? data : []))
      .catch(() => setLokasyonlar([]));

    fetch(`${API_URL}/api/malzemeler`, { headers })
      .then(res => res.json())
      .then(data => setMalzemeler(Array.isArray(data) ? data : []))
      .catch(() => setMalzemeler([]));
  }, []);

  // Filtre (URL) değişince hareketleri yeniden çek — mount'ta URL'deki değerlerle çeker
  // (deep-link / geri dönüş filtreleri otomatik uygulanır).
  useEffect(() => {
    fetchHareketler({
      lokasyon_id: lokasyonId,
      malzeme_id: malzemeId,
      hareket_tipi: hareketTipi,
      baslangic_tarihi: baslangic,
      bitis_tarihi: bitis,
    });
  }, [lokasyonId, malzemeId, hareketTipi, baslangic, bitis, fetchHareketler]);

  const handleFiltreChange = (field, value) => {
    FILTRE_SETTERS[field](value);
  };

  const handleManuelKaydet = async () => {
    if (!manuelForm.lokasyon_id || !manuelForm.malzeme_id || !manuelForm.miktar) {
      setMessage({ text: 'Lütfen tum zorunlu alanlari doldurun.', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/stok-hareketleri`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          lokasyon_id: parseInt(manuelForm.lokasyon_id),
          malzeme_id: parseInt(manuelForm.malzeme_id),
          hareket_tipi: manuelForm.hareket_tipi,
          miktar: parseFloat(manuelForm.miktar),
          açıklama: manuelForm.açıklama || null,
          kullanici_id: user?.id || 1
        })
      });

      if (response.ok) {
        const result = await response.json();
        invalidateQueries(['stok', 'dashboard']);
        setMessage({ text: result.message || 'Stok hareketi kaydedildi', type: 'success' });
        setShowModal(false);
        setManuelForm({ lokasyon_id: '', malzeme_id: '', hareket_tipi: 'SAYIM_FARKI', miktar: '', açıklama: '' });
        fetchHareketler(filtre);
      } else {
        const error = await response.json();
        setMessage({ text: `Hata: ${error.detail}`, type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Baglanti hatası: ' + err.message, type: 'error' });
    }
  };

  // Secilen malzemenin birimi
  const selectedMalzemeBirim = manuelForm.malzeme_id
    ? malzemeler.find(m => String(m.id) === String(manuelForm.malzeme_id))?.birim || 'KG'
    : 'KG';

  const hareketTipleri = ['GIRIS', 'CIKIS', 'SAYIM_FARKI', 'FIRE', 'URETIM_GIRIS', 'URETIM_CIKIS', 'SATIS'];

  return (
    <div>
      <Header title="Stok Hareketleri" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Mesaj */}
        {message.text && (
          <div style={{
            padding: '12px 16px', marginBottom: '15px', borderRadius: radius.md,
            background: message.type === 'success' ? '#d4edda' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            {message.text}
            <button onClick={() => setMessage({ text: '', type: '' })} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'inherit'
            }}>X</button>
          </div>
        )}

        {/* Filtre Bari */}
        <div style={{
          background: colors.surface, padding: '15px 20px', borderRadius: radius.md,
          marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center',
          flexWrap: 'wrap', boxShadow: shadows.sm
        }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Lokasyon</label>
            <select
              value={filtre.lokasyon_id}
              onChange={(e) => handleFiltreChange('lokasyon_id', e.target.value)}
              style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, minWidth: '150px' }}
            >
              <option value="">Tumu</option>
              {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Malzeme</label>
            <select
              value={filtre.malzeme_id}
              onChange={(e) => handleFiltreChange('malzeme_id', e.target.value)}
              style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, minWidth: '150px' }}
            >
              <option value="">Tumu</option>
              {malzemeler.map(m => <option key={m.id} value={m.id}>{m.ad}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Hareket Tipi</label>
            <select
              value={filtre.hareket_tipi}
              onChange={(e) => handleFiltreChange('hareket_tipi', e.target.value)}
              style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, minWidth: '150px' }}
            >
              <option value="">Tumu</option>
              {hareketTipleri.map(t => <option key={t} value={t}>{HAREKET_TIPI_LABELS[t] || t}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Baslangic</label>
            <input
              type="date"
              value={filtre.baslangic_tarihi}
              onChange={(e) => handleFiltreChange('baslangic_tarihi', e.target.value)}
              style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Bitis</label>
            <input
              type="date"
              value={filtre.bitis_tarihi}
              onChange={(e) => handleFiltreChange('bitis_tarihi', e.target.value)}
              style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm }}
            />
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                const rows = [['Hareket No', 'Tarih', 'Lokasyon', 'Malzeme', 'Tip', 'Miktar', 'Birim', 'Önceki', 'Sonraki', 'Referans', 'Açıklama']];
                hareketler.forEach(h => {
                  rows.push([
                    h.hareket_no || '', h.hareket_tarihi ? new Date(h.hareket_tarihi).toLocaleString('tr-TR') : '',
                    h.lokasyon_ad || '', h.malzeme_ad || '', HAREKET_TIPI_LABELS[h.hareket_tipi] || h.hareket_tipi,
                    h.miktar, getBirimLabel(h.birim), h.onceki_stok, h.sonraki_stok,
                    `${h.referans_tipi || ''}${h.referans_id > 0 ? '-' + h.referans_id : ''}`, h.açıklama || ''
                  ]);
                });
                const csv = rows.map(r => r.join(';')).join('\n');
                const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url;
                a.download = `stok-hareketleri-${new Date().toISOString().split('T')[0]}.csv`;
                a.click(); URL.revokeObjectURL(url);
              }}
              style={{
                padding: '10px 16px', background: '#1976d2', color: 'white',
                border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '13px'
              }}
            >
              CSV Indir
            </button>
            <button
              onClick={() => setShowModal(true)}
              style={{
                padding: '10px 20px', background: styles.success, color: 'white',
                border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold'
              }}
            >
              Manuel Hareket Ekle
            </button>
          </div>
        </div>

        {/* Sonuc Sayısı */}
        {!loading && (
          <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '8px' }}>
            {hareketler.length} hareket gosteriliyor
          </div>
        )}

        {/* Tablo */}
        <div style={{
          background: colors.surface, borderRadius: radius.md, overflow: 'hidden',
          boxShadow: shadows.sm
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: styles.primary, color: 'white' }}>
                  <th style={thStyle}>Hareket No</th>
                  <th style={thStyle}>Tarih</th>
                  <th style={thStyle}>Lokasyon</th>
                  <th style={thStyle}>Malzeme</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Tip</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Miktar</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Önceki</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Sonraki</th>
                  <th style={thStyle}>Referans</th>
                  <th style={thStyle}>Açıklama</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="10" style={{ padding: '30px', textAlign: 'center' }}>Yükleniyor...</td></tr>
                ) : hareketler.length === 0 ? (
                  <tr><td colSpan="10" style={{ padding: '30px', textAlign: 'center', color: colors.textTertiary }}>Kayit bulunamadı.</td></tr>
                ) : hareketler.map((h, i) => {
                  const birim = getBirimLabel(h.birim);
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{h.hareket_no}</td>
                      <td style={tdStyle}>{h.hareket_tarihi ? new Date(h.hareket_tarihi).toLocaleString('tr-TR') : '-'}</td>
                      <td style={tdStyle}>{h.lokasyon_ad || '-'}</td>
                      <td style={tdStyle}>{h.malzeme_ad || '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 8px', borderRadius: radius.sm, fontSize: '11px', fontWeight: 'bold',
                          background: h.miktar >= 0 ? '#d4edda' : '#f8d7da',
                          color: h.miktar >= 0 ? '#155724' : '#721c24'
                        }}>{HAREKET_TIPI_LABELS[h.hareket_tipi] || h.hareket_tipi}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: h.miktar >= 0 ? styles.success : styles.danger }}>
                        {h.miktar >= 0 ? '+' : ''}{h.miktar} {birim}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: colors.textSecondary }}>{h.onceki_stok} {birim}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{h.sonraki_stok} {birim}</td>
                      <td style={{ ...tdStyle, fontSize: '12px' }}>{h.referans_tipi}{h.referans_id > 0 ? `-${h.referans_id}` : ''}</td>
                      <td style={{ ...tdStyle, fontSize: '12px', color: colors.textSecondary, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.açıklama || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Manuel Hareket Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000
        }} onClick={() => setShowModal(false)}>
          <div style={{
            background: colors.surface, borderRadius: radius.lg, padding: '30px',
            width: '500px', maxWidth: '92vw', boxSizing: 'border-box', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: styles.secondary }}>Manuel Stok Hareketi</h3>
              <button onClick={() => setShowModal(false)} style={{
                background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: colors.textTertiary
              }}>X</button>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={modalLabelStyle}>Lokasyon *</label>
              <select value={manuelForm.lokasyon_id} onChange={(e) => setManuelForm({ ...manuelForm, lokasyon_id: e.target.value })} style={modalInputStyle}>
                <option value="">Seçiniz</option>
                {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={modalLabelStyle}>Malzeme *</label>
              <select value={manuelForm.malzeme_id} onChange={(e) => setManuelForm({ ...manuelForm, malzeme_id: e.target.value })} style={modalInputStyle}>
                <option value="">Seçiniz</option>
                {malzemeler.map(m => <option key={m.id} value={m.id}>{m.ad} ({getBirimLabel(m.birim)})</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={modalLabelStyle}>Hareket Tipi *</label>
              <select value={manuelForm.hareket_tipi} onChange={(e) => setManuelForm({ ...manuelForm, hareket_tipi: e.target.value })} style={modalInputStyle}>
                <option value="SAYIM_FARKI">Sayim Farki (Stok artir)</option>
                <option value="FIRE">Fire (Stok azalt)</option>
                <option value="GIRIS">Giriş (Diger)</option>
                <option value="CIKIS">Çıkış (Diger)</option>
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={modalLabelStyle}>Miktar ({getBirimLabel(selectedMalzemeBirim)}) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={manuelForm.miktar}
                onChange={(e) => setManuelForm({ ...manuelForm, miktar: e.target.value })}
                style={modalInputStyle}
                placeholder="Orn: 10"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={modalLabelStyle}>Açıklama</label>
              <textarea
                value={manuelForm.açıklama}
                onChange={(e) => setManuelForm({ ...manuelForm, açıklama: e.target.value })}
                rows={2}
                style={{ ...modalInputStyle, resize: 'vertical' }}
                placeholder="Opsiyonel açıklama..."
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleManuelKaydet} style={{
                flex: 1, padding: '12px', background: styles.success, color: 'white',
                border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold'
              }}>KAYDET</button>
              <button onClick={() => setShowModal(false)} style={{
                flex: 1, padding: '12px', background: colors.backgroundAlt, color: '#333',
                border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold'
              }}>IPTAL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '8px', textAlign: 'left' };
const tdStyle = { padding: '12px', fontSize: '14px', verticalAlign: 'middle' };
const modalLabelStyle = { display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px', color: '#555' };
const modalInputStyle = {
  width: '100%', padding: '10px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, boxSizing: 'border-box', fontSize: '14px'
};

export default StokHareketleriPage;
